import { useEffect, useMemo, useState } from "react";
import {
  Background, ControlButton, Controls, Handle, Position, ReactFlow, useNodesState,
  type Connection, type Edge, type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Dagre from "@dagrejs/dagre";
import { InfoDot } from "./labels";
import { useGraphFullscreen } from "./useGraphFullscreen";
import type { ProcessTemplate, ProcessTemplateDep, ProcessTemplateStep } from "./types";

export const LOCATION_NODE = "uzgodnienie_lokalizacji";
const LOC_ID = `key:${LOCATION_NODE}`;

const NODE_W = 200;

const EMPTY_STEP: ProcessTemplateStep = {
  key: "", label: "", submit_label: "", resolve_label: "",
  submit_sla_days: 7, resolve_sla_days: 0, order: 0,
};

type TplNodeData = {
  label: string;
  stepKey: string;
  submitLine: string | null;
  resolveLine: string | null;
  synthetic: boolean;
  isSelected: boolean;
  isCritical: boolean;
};

const _lines = (text: string, perLine: number) => Math.max(1, Math.ceil((text ?? "").length / perLine));

function nodeHeight(d: TplNodeData): number {
  let h = 20; // padding
  h += _lines(d.label, 22) * 15;
  h += 13; // klucz
  if (d.submitLine) h += _lines(d.submitLine, 32) * 13 + 2;
  if (d.resolveLine) h += _lines(d.resolveLine, 32) * 13 + 2;
  return h + 10;
}

function TplStepNode({ data }: { data: TplNodeData }) {
  return (
    <div
      className="rounded-lg border shadow-sm text-xs"
      style={{
        width: NODE_W, padding: "8px 12px",
        background: data.synthetic ? "#f5f3ff" : "#f8fafc",
        color: data.synthetic ? "#5b21b6" : "#334155",
        borderColor: data.isSelected ? "var(--color-primary)" : data.synthetic ? "#7c3aed" : data.isCritical ? "#ea580c" : "rgba(0,0,0,0.15)",
        borderWidth: data.isSelected || data.synthetic || data.isCritical ? 2 : 1,
        borderStyle: data.synthetic ? "dashed" : "solid",
        cursor: "pointer",
      }}
    >
      <Handle
        type="target" position={Position.Left}
        title="Wejście (od czego zależy) — przeciągnij z kropki innego kroku"
        style={{ background: "#64748b", width: 12, height: 12, border: "2px solid #fff", cursor: "pointer" }}
      />
      <div className="font-bold whitespace-normal break-words leading-tight">
        {data.label || <span className="opacity-50 italic">(bez nazwy)</span>}
        {data.isCritical && <span title="Ścieżka krytyczna (najdłuższa suma SLA)" style={{ color: "#ea580c" }}> ◆</span>}
      </div>
      <div className="font-mono text-[10px] opacity-60">
        {data.stepKey || "(bez klucza)"}{data.synthetic ? " · syntetyczny" : ""}
      </div>
      {data.submitLine && <div className="text-[10px] whitespace-normal break-words leading-tight mt-0.5">1▸ {data.submitLine}</div>}
      {data.resolveLine && <div className="text-[10px] whitespace-normal break-words leading-tight opacity-80">2▸ {data.resolveLine}</div>}
      <Handle
        type="source" position={Position.Right}
        title="Wyjście (co od tego zależy) — przeciągnij na kropkę innego kroku"
        style={{ background: "#0ea5e9", width: 12, height: 12, border: "2px solid #fff", cursor: "pointer" }}
      />
    </div>
  );
}

const nodeTypes = { tplStep: TplStepNode };

// Adding edge "node depends_on dependsOn" creates a cycle iff dependsOn is already
// (transitively) waiting for node.
function wouldCycle(deps: ProcessTemplateDep[], node: string, dependsOn: string): boolean {
  const adj = new Map<string, string[]>();
  deps.forEach((d) => adj.set(d.depends_on, [...(adj.get(d.depends_on) ?? []), d.node]));
  const stack = [node];
  const seen = new Set<string>();
  while (stack.length) {
    const cur = stack.pop()!;
    if (cur === dependsOn) return true;
    if (seen.has(cur)) continue;
    seen.add(cur);
    stack.push(...(adj.get(cur) ?? []));
  }
  return false;
}

// Najdłuższa ścieżka DAG po sumie SLA obu faz (waga węzła = SLA1+SLA2, syntetyk = 0).
// Edytor nie dopuszcza cykli, ale strażnik `visiting` chroni przed pętlą nieskończoną.
// (eksport: używa jej też read-only MilestoneSequenceBoard w zakładce „Terminy")
export function criticalPath(steps: ProcessTemplateStep[], deps: ProcessTemplateDep[]):
  { days: number; path: string[]; labels: string[] } | null {
  const weight = new Map<string, number>();
  const labelOf = new Map<string, string>();
  steps.forEach((s) => {
    if (!s.key || weight.has(s.key)) return;
    weight.set(s.key, (s.submit_sla_days || 0) + (s.resolve_sla_days || 0));
    labelOf.set(s.key, s.label || s.key);
  });
  weight.set(LOCATION_NODE, 0);
  labelOf.set(LOCATION_NODE, "uzgodnienie lokalizacji");
  const preds = new Map<string, string[]>();
  deps.forEach((d) => {
    if (weight.has(d.node) && weight.has(d.depends_on)) preds.set(d.node, [...(preds.get(d.node) ?? []), d.depends_on]);
  });
  const memo = new Map<string, { days: number; path: string[] }>();
  const visiting = new Set<string>();
  const best = (k: string): { days: number; path: string[] } => {
    const cached = memo.get(k);
    if (cached) return cached;
    if (visiting.has(k)) return { days: 0, path: [] };
    visiting.add(k);
    let prev = { days: 0, path: [] as string[] };
    (preds.get(k) ?? []).forEach((p) => { const r = best(p); if (r.days > prev.days) prev = r; });
    const res = { days: prev.days + (weight.get(k) ?? 0), path: [...prev.path, k] };
    visiting.delete(k);
    memo.set(k, res);
    return res;
  };
  let top: { days: number; path: string[] } | null = null;
  [...weight.keys()].forEach((k) => { const r = best(k); if (!top || r.days > top.days) top = r; });
  if (!top) return null;
  const found: { days: number; path: string[] } = top;
  if (found.days <= 0) return null;
  return { ...found, labels: found.path.map((k) => labelOf.get(k) ?? k) };
}

function layout(
  draft: ProcessTemplate, selected: number | null,
  critKeys: Set<string>, critEdges: Set<string>,
): { nodes: Node[]; edges: Edge[] } {
  const datas: { id: string; nodeKey: string | null; data: TplNodeData }[] = draft.steps.map((s, i) => ({
    id: `s${i}`,
    nodeKey: s.key || null,
    data: {
      label: s.label,
      stepKey: s.key,
      submitLine: s.submit_label ? `${s.submit_label} (${s.submit_sla_days}d)` : null,
      resolveLine: s.resolve_label ? `${s.resolve_label} (${s.resolve_sla_days}d)` : null,
      synthetic: false,
      isSelected: selected === i,
      isCritical: !!s.key && critKeys.has(s.key),
    },
  }));
  datas.push({
    id: LOC_ID,
    nodeKey: LOCATION_NODE,
    data: {
      label: "Uzgodnienie lokalizacji", stepKey: LOCATION_NODE,
      submitLine: "węzeł syntetyczny (działki/WPW)", resolveLine: null,
      synthetic: true, isSelected: false, isCritical: critKeys.has(LOCATION_NODE),
    },
  });

  // key → node id (pierwszy krok o danym kluczu; duplikaty łapie walidacja w panelu)
  const byKey = new Map<string, string>();
  draft.steps.forEach((s, i) => { if (s.key && !byKey.has(s.key)) byKey.set(s.key, `s${i}`); });
  byKey.set(LOCATION_NODE, LOC_ID);

  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 26, ranksep: 80, marginx: 12, marginy: 12 });
  datas.forEach((n) => g.setNode(n.id, { width: NODE_W, height: nodeHeight(n.data) }));
  const edges: Edge[] = [];
  draft.dependencies.forEach((d, i) => {
    const source = byKey.get(d.depends_on);
    const target = byKey.get(d.node);
    if (!source || !target) return; // zależność do nieistniejącego klucza — niewidoczna, zniknie przy usuwaniu kroku
    g.setEdge(source, target);
    const onCritical = critEdges.has(`${d.depends_on}->${d.node}`);
    edges.push({
      id: `d${i}`, source, target,
      animated: onCritical,
      style: { stroke: onCritical ? "#ea580c" : "#94a3b8", strokeWidth: onCritical ? 2.5 : 1.5 },
      markerEnd: { type: "arrowclosed" as const, color: onCritical ? "#ea580c" : "#94a3b8" },
      data: { node: d.node, depends_on: d.depends_on },
    });
  });
  Dagre.layout(g);
  const nodes: Node[] = datas.map((n) => {
    const p = g.node(n.id);
    // Ręcznie przesunięty kafelek (zapamiętany po kluczu) honoruje x/y; reszta = dagre.
    const saved = n.nodeKey ? draft.layout?.[n.nodeKey] : undefined;
    return {
      id: n.id, type: "tplStep",
      position: saved
        ? { x: saved.x, y: saved.y }
        : { x: p.x - NODE_W / 2, y: p.y - nodeHeight(n.data) / 2 },
      data: n.data as unknown as Record<string, unknown>,
    };
  });
  return { nodes, edges };
}

export function TemplateGraphEditor({ draft, onDraft, keySuggestions = [] }: {
  draft: ProcessTemplate;
  onDraft: (next: ProcessTemplate) => void;
  // podpowiedzi kluczy (wbudowane kamienie + klucze z innych szablonów) do datalisty
  keySuggestions?: { key: string; label: string }[];
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { isFull, toggle: toggleFull } = useGraphFullscreen();

  const crit = useMemo(() => criticalPath(draft.steps, draft.dependencies), [draft]);
  const laid = useMemo(() => {
    const critKeys = new Set(crit?.path ?? []);
    const critEdges = new Set((crit?.path ?? []).slice(1).map((k, i) => `${crit!.path[i]}->${k}`));
    return layout(draft, selected, critKeys, critEdges);
  }, [draft, selected, crit]);
  // Węzły trzymamy w stanie React Flow (przeciąganie); resync do układu przy zmianie draftu.
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  useEffect(() => setNodes(laid.nodes), [laid, setNodes]);
  const edges = laid.edges;

  const idToKey = (id: string): string | null => {
    if (id === LOC_ID) return LOCATION_NODE;
    const step = draft.steps[Number(id.slice(1))];
    return step?.key || null;
  };

  const onConnect = (conn: Connection) => {
    setErr(null);
    if (!conn.source || !conn.target || conn.source === conn.target) return;
    const dependsOn = idToKey(conn.source);
    const node = idToKey(conn.target);
    if (!dependsOn || !node) { setErr("Nadaj krokowi klucz, zanim go połączysz."); return; }
    if (draft.dependencies.some((d) => d.node === node && d.depends_on === dependsOn)) return;
    if (wouldCycle(draft.dependencies, node, dependsOn)) {
      setErr(`Nie można: „${node} ← ${dependsOn}” utworzyłoby cykl.`);
      return;
    }
    onDraft({ ...draft, dependencies: [...draft.dependencies, { node, depends_on: dependsOn }] });
  };

  const setStep = (idx: number, patch: Partial<ProcessTemplateStep>) => {
    let deps = draft.dependencies;
    let layout = draft.layout;
    // zmiana klucza przenosi zależności ORAZ zapisaną pozycję na nowy klucz
    if (patch.key !== undefined) {
      const oldKey = draft.steps[idx].key;
      if (oldKey && patch.key !== oldKey) {
        deps = deps.map((d) => ({
          node: d.node === oldKey ? patch.key! : d.node,
          depends_on: d.depends_on === oldKey ? patch.key! : d.depends_on,
        }));
        if (layout?.[oldKey]) {
          const { [oldKey]: moved, ...rest } = layout;
          layout = { ...rest, [patch.key!]: moved };
        }
      }
    }
    onDraft({
      ...draft,
      dependencies: deps,
      layout,
      steps: draft.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    });
  };

  const removeStep = (idx: number) => {
    const key = draft.steps[idx].key;
    let layout = draft.layout;
    if (key && layout?.[key]) {
      const { [key]: _drop, ...rest } = layout;
      layout = rest;
    }
    onDraft({
      ...draft,
      steps: draft.steps.filter((_, i) => i !== idx),
      dependencies: key
        ? draft.dependencies.filter((d) => d.node !== key && d.depends_on !== key)
        : draft.dependencies,
      layout,
    });
    setSelected(null);
  };

  const addStep = () => {
    onDraft({ ...draft, steps: [...draft.steps, { ...EMPTY_STEP, order: draft.steps.length }] });
    setSelected(draft.steps.length);
  };

  const sel = selected != null ? draft.steps[selected] : null;
  const dupKey = sel?.key ? draft.steps.some((s, i) => i !== selected && s.key === sel.key) : false;

  return (
    <div className={isFull ? "fixed inset-0 z-40 bg-[var(--color-bg-base)] p-4 flex flex-col overflow-auto" : ""}>
      <div className={`${isFull ? "flex-1 min-h-[280px]" : "h-[420px]"} rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)]`}>
        <ReactFlow
          key={isFull ? "fs" : "inline"}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          colorMode="system"
          proOptions={{ hideAttribution: true }}
          nodesConnectable
          connectOnClick
          onConnect={onConnect}
          onNodesChange={onNodesChange}
          onNodeDragStop={(_e, node) => {
            // Przeciągnięto kafelek → zapisz pozycję w szablonie (po stabilnym kluczu).
            // Krok bez klucza nie ma gdzie jej zaczepić — pomijamy (dagre go ułoży).
            const key = idToKey(node.id);
            if (!key) return;
            onDraft({
              ...draft,
              layout: { ...(draft.layout ?? {}), [key]: { x: Math.round(node.position.x), y: Math.round(node.position.y) } },
            });
          }}
          onEdgeClick={(_e, edge) => {
            const d = edge.data as { node: string; depends_on: string } | undefined;
            if (d && window.confirm(`Usunąć zależność „${d.node} ← ${d.depends_on}”?`)) {
              onDraft({
                ...draft,
                dependencies: draft.dependencies.filter((x) => !(x.node === d.node && x.depends_on === d.depends_on)),
              });
            }
          }}
          onNodeClick={(_e, n) => {
            if (n.id === LOC_ID) { setSelected(null); return; }
            setSelected(Number(n.id.slice(1)));
          }}
          onPaneClick={() => setSelected(null)}
        >
          <Background gap={16} />
          <Controls showInteractive={false}>
            {draft.layout && Object.keys(draft.layout).length > 0 && (
              <ControlButton
                onClick={() => {
                  if (window.confirm("Rozmieścić kafelki automatycznie (dagre)?\n\nZapisany układ szablonu zostanie usunięty."))
                    onDraft({ ...draft, layout: {} });
                }}
                title="Auto-rozmieszczenie — usuń zapisany układ szablonu"
              >
                ▦
              </ControlButton>
            )}
            <ControlButton onClick={toggleFull} title={isFull ? "Zamknij pełny ekran (Esc)" : "Pełny ekran"}>
              {isFull ? "✕" : "⛶"}
            </ControlButton>
          </Controls>
        </ReactFlow>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-2">
        <button
          type="button"
          className="text-xs font-mono px-2 py-1 border border-dashed border-[var(--color-border)] text-[var(--color-text-muted)]"
          onClick={addStep}
        >
          + dodaj krok
        </button>
        <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
          klik krok = edycja · przeciągnij kropki = zależność (krok ← wymaga wcześniej) · przeciągnij kafelek = przestaw (układ zapisze się w szablonie i przejdzie do projektów) · klik krawędź = usuń zależność
        </span>
        {err && <span className="text-xs text-[var(--color-primary)]">{err}</span>}
      </div>
      {crit && (
        <p className="text-[10px] font-mono mt-1" style={{ color: "#ea580c" }}>
          ⏱ ścieżka krytyczna: ~{crit.days} dni — {crit.labels.join(" → ")}
        </p>
      )}
      <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-1">
        „{LOCATION_NODE}” to węzeł syntetyczny (uzgodnienia lokalizacji z działek/WPW) — można go łączyć w zależnościach, ale nie edytować.
      </p>

      {sel && selected != null && (
        <div className="mt-3 p-3 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)]">
          <div className="section-label mb-2">Krok: {sel.label || sel.key || "(nowy)"}</div>
          <div className="flex flex-wrap items-end gap-2 text-sm">
            <label className="flex flex-col gap-1">
              <span className="section-label">Klucz<InfoDot text="Stały identyfikator kroku (np. zud, dane, audyt-ze). Dla wbudowanych kroków użyj istniejących kluczy (podpowiedzi), żeby stany projektów przetrwały reset — literówka tworzy NOWY byt. Zmiana klucza przenosi zależności." /></span>
              <input className="input-base w-32 font-mono" list="tpl-key-suggestions" value={sel.key} onChange={(e) => setStep(selected, { key: e.target.value })} />
              <datalist id="tpl-key-suggestions">
                {keySuggestions.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </datalist>
            </label>
            <label className="flex flex-col gap-1">
              <span className="section-label">Nazwa kroku</span>
              <input className="input-base w-44" value={sel.label} onChange={(e) => setStep(selected, { label: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="section-label">Faza 1 — nasza akcja<InfoDot text="Co MY robimy w tym kroku (np. „Zamówienie danych (my)”)." /></span>
              <input className="input-base w-56" value={sel.submit_label} onChange={(e) => setStep(selected, { submit_label: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="section-label">SLA1 [dni]</span>
              <input type="number" className="input-base w-20" value={sel.submit_sla_days} onChange={(e) => setStep(selected, { submit_sla_days: Number(e.target.value) })} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="section-label">Faza 2 — odpowiedź strony<InfoDot text="Na co czekamy od drugiej strony (np. „Dostarczenie danych (ZE)”). Puste = krok w całości po naszej stronie." /></span>
              <input className="input-base w-56" value={sel.resolve_label} onChange={(e) => setStep(selected, { resolve_label: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="section-label">SLA2 [dni]</span>
              <input type="number" className="input-base w-20" value={sel.resolve_sla_days} onChange={(e) => setStep(selected, { resolve_sla_days: Number(e.target.value) })} />
            </label>
            <button
              type="button"
              className="text-[var(--color-primary)] text-xs ml-auto"
              onClick={() => {
                if (window.confirm(`Usunąć krok „${sel.label || sel.key || "(nowy)"}” wraz z jego zależnościami?`)) removeStep(selected);
              }}
            >
              usuń krok
            </button>
          </div>
          {dupKey && <p className="text-xs text-[var(--color-primary)] mt-1">Ten klucz ma już inny krok — klucze muszą być unikalne.</p>}
          {sel.key === LOCATION_NODE && <p className="text-xs text-[var(--color-primary)] mt-1">„{LOCATION_NODE}” jest zarezerwowany dla węzła syntetycznego.</p>}
        </div>
      )}
    </div>
  );
}
